import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, forkJoin, of, switchMap } from 'rxjs';
import { MaterialsService } from '../../../../services/materials.service';
import { JobRequestsService } from '../../../../services/job-requests.service';
import { SystemSettingsService } from '../../../../services/system-settings.service';
import {
  ChemicalLookupItem,
  MaterialLookupsDto,
} from '@models/material-lookups-dto.interface';
import { MaterialForEditDto } from '@models/material-for-edit-dto.interface';
import { CisProLookupDto } from '@models/cis-pro-lookup-dto.interface';
import { SaveMaterialDto } from '@models/save-material-dto.interface';
import { SystemSettingDto } from '@models/system-setting-dto.interface';
import { SampleManagerLookupComponent } from '@shared/sample-manager-lookup/sample-manager-lookup.component';

interface EditMaterialDialogData {
  materialId?: number;
  jobRequestId?: number;
}

@Component({
  selector: 'app-edit-material-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    SampleManagerLookupComponent,
  ],
  templateUrl: './edit-material-dialog.component.html',
  styleUrl: './edit-material-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditMaterialDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<EditMaterialDialogComponent>);
  private readonly data = inject<EditMaterialDialogData>(MAT_DIALOG_DATA);
  private readonly materialsService = inject(MaterialsService);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly systemSettingsService = inject(SystemSettingsService);
  private readonly fb = inject(FormBuilder);

  protected isLoading = signal(true);
  protected isSaving = signal(false);

  protected isPullingCisPro = signal(false);
  protected errorMessage = signal('');

  protected lookups = signal<MaterialLookupsDto | null>(null);
  protected cisProLinked = signal(false);
  protected selectedChemical = signal<ChemicalLookupItem | null>(null);
  protected sgSignal = signal<number | null>(null);

  protected isProduct = computed(() => this.selectedChemical()?.materialTypeId === 2);
  protected isMud = computed(() => this.selectedChemical()?.materialTypeId === 1);
  protected mudChemicals = computed(() => this.lookups()?.chemicals.filter(c => c.materialTypeId === 1) ?? []);

  protected get isEditMode(): boolean {
    return this.data != null && this.data.materialId != null;
  }

  protected form: FormGroup = this.fb.group({
    barcode: [null as string | null],
    chemicalId: [null as number | null],
    rigId: [null as number | null],
    wellId: [null as number | null],
    supplierId: [null as number | null],
    cisProMaterialId: [null as number | null],
    specificGravity: [null as number | null],
    ppg: [null as number | null],
    bariteSpecificGravity: [null as number | null],
    baseFluidSpecificGravity: [null as number | null],
    lowGravitySolidsSpecificGravity: [null as number | null],
    receivedOn: [null as string | null, Validators.required],
    sampledOn: [null as string | null],
    splNo: [null as string | null],
    usageDescription: [null as string | null],
    comments: [null as string | null],
    contact: [null as string | null],
  });

  ngOnInit(): void {
    this.setupFormSubscriptions();
    this.loadInitialData();
  }

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select[formControlName]:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  private setupFormSubscriptions(): void {
    this.form.get('chemicalId')!.valueChanges.subscribe((id: number | null) => {
      const chemical = this.lookups()?.chemicals.find(c => c.id === id) ?? null;
      this.selectedChemical.set(chemical);
      if (chemical?.specificGravity != null && !this.form.get('specificGravity')?.value) {
        this.form.get('specificGravity')!.setValue(chemical.specificGravity);
      }
      this.applyFluidSgDefaults(chemical);
      this.updateReceivedOnValidators();
      this.errorMessage.set('');
    });

    this.form.get('specificGravity')!.valueChanges.subscribe(sg => {
      this.sgSignal.set(sg);
      this.form.get('ppg')!.setValue(this.sgToPpg(sg), { emitEvent: false });
    });

    this.form.get('ppg')!.valueChanges.subscribe(ppg => {
      const specificGravity = this.ppgToSg(ppg);
      this.sgSignal.set(specificGravity);
      this.form.get('specificGravity')!.setValue(specificGravity, { emitEvent: false });
    });

  }

  private sgToPpg(sg: number | null | undefined): number | null {
    if (sg == null || sg <= 0) return null;
    return Math.round(sg * 8.345 * 1000) / 1000;
  }

  private ppgToSg(ppg: number | null | undefined): number | null {
    if (ppg == null || ppg <= 0) return null;
    return Math.round((ppg / 8.345) * 1000) / 1000;
  }


  private updateReceivedOnValidators(): void {
    const control = this.form.get('receivedOn')!;
    if (this.isProduct()) {
      control.clearValidators();
    } else {
      control.setValidators(Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }


  private applyFluidSgDefaults(chemical: ChemicalLookupItem | null): void {
    if (this.isEditMode || chemical?.name == null) return;

    const name = chemical.name.toUpperCase();
    let fluidType: 'WBM' | 'SOBM' | 'DOBM' | null = null;

    // Determine fluid type from chemical name
    if (name.includes('WBM')) fluidType = 'WBM';
    else if (name.includes('SOBM')) fluidType = 'SOBM';
    else if (name.includes('DOBM')) fluidType = 'DOBM';

    if (!fluidType) return;

    // Get fluid constants from system settings
    this.systemSettingsService.getFluidConstants(fluidType).subscribe({
      next: (constants) => {
        this.form.patchValue({
          bariteSpecificGravity: constants.bariteSG,
          baseFluidSpecificGravity: constants.baseFluidSG,
          lowGravitySolidsSpecificGravity: constants.lowGravitySolidsSG,
        }, { emitEvent: false });
      },
      error: () => {
        // If system settings don't have the value, use fallback hardcoded defaults
        const defaults: Record<string, Record<string, number>> = {
          'WBM': { bariteSpecificGravity: 4.2, baseFluidSpecificGravity: 1.0, lowGravitySolidsSpecificGravity: 2.6 },
          'SOBM': { bariteSpecificGravity: 4.2, baseFluidSpecificGravity: 0.79, lowGravitySolidsSpecificGravity: 2.6 },
          'DOBM': { bariteSpecificGravity: 4.1, baseFluidSpecificGravity: 0.84, lowGravitySolidsSpecificGravity: 2.6 }
        };
        const defaultValues = defaults[fluidType!];
        if (defaultValues) {
          this.form.patchValue(defaultValues, { emitEvent: false });
        }
      }
    });
  }

  private loadInitialData(): void {
    const lookupsObs = this.materialsService.getLookups();

    if (this.isEditMode) {
      forkJoin({
        lookups: lookupsObs,
        material: this.materialsService.getMaterialForEdit(this.data.materialId!),
      }).subscribe({
        next: ({ lookups, material }) => {
          this.lookups.set(lookups);
          this.patchFormForEdit(material);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      });
    } else {
      forkJoin({
        lookups: lookupsObs,
        defaultSupplier: this.systemSettingsService.getSettingByKey('DEFAULT_SUPPLIER').pipe(
          catchError(() => of(null as SystemSettingDto | null))
        ),
      }).subscribe({
        next: ({ lookups, defaultSupplier }) => {
          this.lookups.set(lookups);
          this.applyDefaultSupplier(defaultSupplier);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      });
    }
  }

  private applyDefaultSupplier(setting: SystemSettingDto | null): void {
    if (!setting || this.form.get('supplierId')?.value != null) return;

    try {
      const supplierId = JSON.parse(setting.settingValue);
      if (typeof supplierId === 'number') {
        this.form.get('supplierId')?.setValue(supplierId);
      }
    } catch (err) {
      console.error('Error parsing default supplier setting:', err);
    }
  }

  private patchFormForEdit(material: MaterialForEditDto): void {
    this.form.patchValue({
      barcode: material.barcode,
      chemicalId: material.chemicalId,
      rigId: material.rigId,
      wellId: material.wellId,
      supplierId: material.supplierId,
      cisProMaterialId: material.cisProMaterialId,
      specificGravity: material.specificGravity,
      bariteSpecificGravity: material.bariteSpecificGravity,
      baseFluidSpecificGravity: material.baseFluidSpecificGravity,
      lowGravitySolidsSpecificGravity: material.lowGravitySolidsSpecificGravity,
      receivedOn: material.receivedOn ? this.toDateInputValue(material.receivedOn) : null,
      sampledOn: material.sampledOn ? this.toDateInputValue(material.sampledOn) : null,
      splNo: material.splNo,
      usageDescription: material.usageDescription,
      comments: material.comments,
      contact: material.contact,
    });

    if (material.cisProMaterialId) {
      this.cisProLinked.set(true);
    }

    this.sgSignal.set(material.specificGravity);
  }

  protected onLookupResult(cispro: CisProLookupDto): void {
    this.applyCisProResult(cispro);
  }

  protected onPullCisPro(): void {
    const barcode = (this.form.get('barcode')?.value as string | null)?.trim();
    if (!barcode) return;

    this.materialsService.getCisProByBarcode(barcode).subscribe({
      next: result => {
        if (result) {
          this.applyCisProResult(result);
        }
      },
    });
  }

  protected onRigChange(_event: Event): void {
    const rigId = this.form.get('rigId')?.value;
    const selectedRigId = typeof rigId === 'number' ? rigId : undefined;

    this.form.get('wellId')?.setValue(null);
    this.materialsService.getLookups(selectedRigId).subscribe({
      next: lookups => this.lookups.set(lookups),
      error: () => {
        this.lookups.update(current => current ? { ...current, wells: [] } : current);
        this.errorMessage.set('Unable to reload wells. Please try again.');
      },
    });
  }

  private applyCisProResult(cispro: CisProLookupDto): void {
    const patch: Record<string, unknown> = { cisProMaterialId: cispro.id };
    if (cispro.barcode) patch['barcode'] = cispro.barcode;
    const supplierDescription = cispro.supplierDescription?.trim().toLowerCase();
    const supplier = supplierDescription
      ? this.lookups()?.suppliers.find(localSupplier => {
          const names = [localSupplier.name, localSupplier.alias]
            .filter((name): name is string => !!name)
            .map(name => name.trim().toLowerCase());
          return names.some(name =>
            supplierDescription === name || supplierDescription.startsWith(`${name} `)
          );
        })
      : null;
    if (supplier) patch['supplierId'] = supplier.id;
    if (cispro.rigId) patch['rigId'] = cispro.rigId;
    if (cispro.receivedDate) patch['receivedOn'] = cispro.receivedDate.substring(0, 10);
    if (cispro.supplierDescription) patch['usageDescription'] = cispro.supplierDescription;

    if (cispro.description) {
      const ppgMatch = /(\d*\.?\d+)\s*PPG/.exec(cispro.description);
      if (ppgMatch) {
        const ppg = parseFloat(ppgMatch[1]);
        if (!isNaN(ppg) && ppg > 0) {
          patch['specificGravity'] = Math.round((ppg / 8.345) * 1000) / 1000;
        }
      }
    }

    if (cispro.notes) {
      const splMatch = /SPL#\s*:?\s*(\d+)/.exec(cispro.notes);
      if (splMatch) patch['splNo'] = splMatch[1];

      const recMatch = /REC'?\s*D?[:;]\s*(\d+\/\d+\/\d+)/i.exec(cispro.notes);
      if (recMatch) patch['receivedOn'] = this.usDateToIso(recMatch[1]);
    }

    if (cispro.materialName && !this.form.get('chemicalId')?.value) {
      const chemical = this.lookups()?.chemicals.find(
        c => c.name.toLowerCase() === cispro.materialName!.toLowerCase()
      );
      if (chemical) patch['chemicalId'] = chemical.id;
    }

    this.form.patchValue(patch);
    this.cisProLinked.set(true);
  }

  private usDateToIso(usDate: string): string {
    const parts = usDate.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    }
    return usDate;
  }

  private toDateInputValue(date: Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  protected onSave(): void {
    if (this.isSaving()) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set('');
    const dto = this.buildDto();

    if (this.isEditMode) {
      this.materialsService.updateMaterial(this.data.materialId!, dto).pipe(
        switchMap(() => this.data.jobRequestId == null
          ? of(void 0)
          : this.jobRequestsService.updateMudCheckConstants(
              this.data.jobRequestId,
              this.buildMudCheckConstantsDto()
            )
        )
      ).subscribe({
        next: () => {
          this.isSaving.set(false);
          this.dialogRef.close(this.data.materialId);
        },
        error: (err: HttpErrorResponse) => {
          this.isSaving.set(false);
          this.handleSaveError(err);
        },
      });
    } else {
      this.materialsService.createMaterial(dto).subscribe({
        next: (newId: number) => {
          this.isSaving.set(false);
          this.dialogRef.close(newId);
        },
        error: (err: HttpErrorResponse) => {
          this.isSaving.set(false);
          this.handleSaveError(err);
        },
      });
    }
  }

  private handleSaveError(err: HttpErrorResponse): void {
    const errors = err.error?.errors as Record<string, string[]> | undefined;
    const barcodeError = errors?.['barcode']?.[0];

    if (barcodeError) {
      this.errorMessage.set(barcodeError);
      return;
    }

    this.errorMessage.set('Save failed. Please check your entries and try again.');
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  private buildDto(): SaveMaterialDto {
    const v = this.form.value;
    return {
      barcode: v.barcode || undefined,
      name: this.lookups()?.chemicals?.find(c => c.id === v.chemicalId)?.name || undefined,
      chemicalId: v.chemicalId || undefined,
      rigId: v.rigId || undefined,
      wellId: v.wellId || undefined,
      supplierId: v.supplierId || undefined,
      cementSupplierId: undefined,
      cisProMaterialId: v.cisProMaterialId || undefined,
      specificGravity: v.specificGravity || undefined,
      bariteSpecificGravity: v.bariteSpecificGravity || undefined,
      baseFluidSpecificGravity: v.baseFluidSpecificGravity || undefined,
      lowGravitySolidsSpecificGravity: v.lowGravitySolidsSpecificGravity || undefined,
      receivedOn: v.receivedOn,
      sampledOn: v.sampledOn,
      splNo: v.splNo || undefined,
      description: this.lookups()?.chemicals?.find(c => c.id === v.chemicalId)?.name || undefined,
      usageDescription: v.usageDescription || undefined,
      comments: v.comments || undefined,
      contact: v.contact || undefined,
    };
  }

  private buildMudCheckConstantsDto(): {
    bariteSgconst: number | null;
    baseFluidSgconst: number | null;
    lowGravitySolidsSgconst: number | null;
  } {
    const v = this.form.getRawValue();
    return {
      bariteSgconst: v.bariteSpecificGravity ?? null,
      baseFluidSgconst: v.baseFluidSpecificGravity ?? null,
      lowGravitySolidsSgconst: v.lowGravitySolidsSpecificGravity ?? null,
    };
  }
}
